import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Typography, CircularProgress } from "@mui/material";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useSelector } from "react-redux";
import { userSelector } from "@/redux/user/user.selector";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { restorePassword } from "@/redux/user/user.action";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
import { RequestStatus } from "@/utilities/ResponseEnum/RequestStatus";

const FormSchema = z
  .object({
    password: z.string().min(6, {
      message: "La contraseña debe tener al menos 6 caracteres.",
    }),
    confirmPassword: z.string().min(6, {
      message: "La confirmación debe tener al menos 6 caracteres.",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden.",
    path: ["confirmPassword"],
  });

export const RecoverPassword = () => {
  const { token } = useParams<{ token: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { loading } = useSelector(userSelector).ui;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const password = data.password;
    if (token && password) {
      const response = await dispatch(
        restorePassword({ password: password, token: token }),
      );
      if (response.meta.requestStatus === RequestStatus.Fulfilled) {
        SuccessToast({ message: "Contraseña actualizada correctamente" });
        form.reset();
        setTimeout(() => navigate("/login"), 500);
      } else {
        ErrorToast({ message: "Error al actualizar la contraseña" });
        form.reset();
      }
    }
  };

  return (
    <>
      <Card className={"w-[25rem] h-fit p-6"}>
        <CardHeader className="flex flex-col pb-0 h-fit gap-1 px-0">
          <CardTitle>
            <Typography
              fontFamily={"Inter"}
              fontSize={"18px"}
              fontWeight={500}
              letterSpacing={0}
            >
              Cambiar contraseña
            </Typography>
          </CardTitle>
          <CardDescription className="text-sm text-gray-500">
            Establece tu nueva contraseña. Por motivos de seguridad, no la
            compartas con terceros.
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-[0.5rem] px-0">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4 flex flex-col justify-between"
            >
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-[14px]">
                      Ingresa tu nueva contraseña
                    </FormLabel>
                    <FormControl>
                      <div className="relative min-w-[100%] max-w-[100%]">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Ingresa tu nueva contraseña"
                          {...field}
                          className="text-[14px] max-w-[100%] min-w-[100%]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                          aria-label="Toggle password visibility"
                        >
                          {showPassword ? (
                            <EyeOff className="size-5 text-[#1F6B45]" />
                          ) : (
                            <Eye className="size-5 text-[#1F6B45]" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-[14px]">
                      Confirma tu nueva contraseña
                    </FormLabel>
                    <FormControl>
                      <div className="relative min-w-[100%] max-w-[100%]">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Ingresa tu nueva contraseña"
                          {...field}
                          className="text-[14px] max-w-[100%] min-w-[100%]"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                          aria-label="Toggle password visibility"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="size-5 text-[#1F6B45]" />
                          ) : (
                            <Eye className="size-5 text-[#1F6B45]" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <CardFooter
                className={`flex w-full gap-4 pt-4 flex-row items-center justify-center flex-wrap sm:flex-row sm:justify-end sm:items-center px-0`}
              >
                <Button
                  variant="contained"
                  className="w-full justify-self-end w-[7.5rem] flex hover:bg-[#1F6B45]/90 shadow-sm"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <CircularProgress size={16} color="inherit" />
                    </div>
                  ) : (
                    <span>Confirmar</span>
                  )}
                </Button>
              </CardFooter>
            </form>
          </Form>
        </CardContent>
      </Card>
    </>
  );
};
